import { API_BASE_URL, REQUEST_TIMEOUT } from '../constants';
import type { User, UserFilters, UsersResponse } from '../types';

class UserService {
  private async fetchWithAuth<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('admin_token');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
          ...options.headers,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({
          message: 'Erro ao processar requisição'
        }));
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Tempo limite da requisição excedido');
        }
        throw error;
      }
      throw new Error('Erro desconhecido');
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async getAllUsers(filters: UserFilters = {}): Promise<UsersResponse> {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (typeof filters.blocked === 'boolean') params.append('blocked', String(filters.blocked));

    const queryString = params.toString();
    const url = `${API_BASE_URL}/admin/users${queryString ? '?' + queryString : ''}`;

    const response = await this.fetchWithAuth<UsersResponse | User[]>(url);

    if (Array.isArray(response)) {
      return {
        users: response,
        data: response,
        total: response.length,
        page: filters.page || 1,
        totalPages: 1,
      };
    }

    return response as UsersResponse;
  }

  async getUserById(id: number): Promise<User> {
    const url = `${API_BASE_URL}/admin/users/${id}`;
    return this.fetchWithAuth<User>(url);
  }

  async updateUserBlockStatus(
    id: number,
    isBlocked: boolean,
    blockedReason?: string,
  ): Promise<User> {
    const url = `${API_BASE_URL}/admin/users/${id}/block`;
    return this.fetchWithAuth<User>(url, {
      method: 'PUT',
      body: JSON.stringify({
        isBlocked,
        blockedReason: blockedReason || undefined,
      }),
    });
  }
}

export const userService = new UserService();
